import React from 'react';
import { UserUsage } from '../../services/regularUserService';

/**
 * نشانِ «این کاربر از کجا استفاده می‌کند».
 *
 * منبع داده `User.usage` است که بک‌اند از روی سشن‌های آنالیتیکس denormalize
 * می‌کند — نه از روی توکن پوش یا User-Agent. یعنی کاربری که فقط اپ را نصب
 * کرده ولی هرگز بازش نکرده، «نامشخص» می‌ماند و این درست است.
 *
 * نبودِ `usage` سه معنی دارد و هر سه یکسان نمایش داده می‌شوند («نامشخص»):
 *  ۱) کاربر از زمان راه‌اندازی این ماژول هیچ فعالیتی نداشته،
 *  ۲) فعالیتش فقط با حساب ادمین بوده (از آمار حذف می‌شود)،
 *  ۳) کاربر جدید است و هنوز اولین سشنش ثبت نشده.
 */

export const PLATFORM_LABEL_FA: Record<string, string> = {
  web: 'وب‌سایت',
  android: 'اپ اندروید',
  ios: 'اپ iOS',
  mobile_app: 'اپلیکیشن',
  admin: 'پنل ادمین',
  telegram: 'تلگرام',
  unknown: 'نامشخص',
};

const CHANNEL_STYLE: Record<string, { label: string; bg: string; fg: string }> = {
  app: { label: 'اپلیکیشن', bg: 'rgba(46, 125, 50, 0.12)', fg: '#2e7d32' },
  web: { label: 'وب‌سایت', bg: 'rgba(25, 118, 210, 0.12)', fg: '#1976d2' },
  both: { label: 'اپ + وب', bg: 'rgba(123, 31, 162, 0.12)', fg: '#7b1fa2' },
  telegram: { label: 'تلگرام', bg: 'rgba(34, 158, 217, 0.12)', fg: '#229ED9' },
  unknown: { label: 'نامشخص', bg: 'rgba(120, 120, 120, 0.12)', fg: '#6b7280' },
};

const chipStyle = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-block',
  background: bg,
  color: fg,
  padding: '2px 8px',
  borderRadius: 10,
  fontSize: 11,
  lineHeight: '18px',
  whiteSpace: 'nowrap',
});

interface Props {
  usage?: UserUsage | null;
  /** نمایش نسخه/مدل دستگاه زیر نشان — برای جدول لیست کاربران */
  showDetail?: boolean;
}

const UserPlatformBadge: React.FC<Props> = ({ usage, showDetail = true }) => {
  const channel = usage?.primaryChannel || 'unknown';
  const style = CHANNEL_STYLE[channel] || CHANNEL_STYLE.unknown;

  // ریزدانه‌ها (اندروید/iOS) کنار نشانِ اصلی می‌آیند تا «اپ + وب» هم بگوید
  // کدام اپ. `admin` عمداً حذف می‌شود: کارمندی که در پنل کار می‌کند کاربر
  // محصول نیست و نمایشش گمراه‌کننده است.
  const platforms = (usage?.platforms || []).filter(
    (p) => p !== 'admin' && p !== 'web' && p !== 'telegram'
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={chipStyle(style.bg, style.fg)}>{style.label}</span>
      {showDetail && platforms.length > 0 && (
        <span style={{ fontSize: 10, color: '#6b7280' }}>
          {platforms.map((p) => PLATFORM_LABEL_FA[p] || p).join('، ')}
        </span>
      )}
      {showDetail && usage?.appVersion && (
        <span style={{ fontSize: 10, color: '#9ca3af' }} title={usage.appDeviceModel || ''}>
          نسخه {usage.appVersion}
          {usage.appDeviceModel ? ` · ${usage.appDeviceModel}` : ''}
        </span>
      )}
    </div>
  );
};

export default UserPlatformBadge;
